import { useCallback, useEffect, useState } from 'react';
import { playerConfigDomain } from '../../../util/config';
import axios from 'axios';
import Cookies from 'js-cookie';

import { ENTERPRISE_CONFIG_ID, isUsedOnMobile, SCENE_EDITOR_PLAYER_ACCOUNT_ID, SCENE_EDITOR_PLAYER_CONFIG_ID, setAdditionalStyleSheet } from '../../../util/util';
import i18n from '../../../i18n';
import { useChatLogin } from '../../../contexts/ChatLoginContext';

interface UseFetchPlayerConfigDataProps {
	playerConfigId: string;
	playerConfigURL?: string;
	accountId: string;
	fromSceneEditor?: boolean;
	configParams?: any;
	isMediaLive?: boolean;
	setLoading?: (_value: boolean) => void;
}

const useFetchPlayerConfigData = ({ playerConfigId, accountId, fromSceneEditor, configParams, isMediaLive, playerConfigURL, setLoading }: UseFetchPlayerConfigDataProps) => {
	const { setUserId, setUsername, setEmail } = useChatLogin();
	const [playerMobileConfig, setPlayerMobileConfig] = useState<any>({});
	const [playerDesktopConfig, setPlayerDesktopConfig] = useState<any>({});

	const [playerConfig, setPlayerConfig] = useState<any>();

	const isIgnoreChatCookies = useCallback((settings: any, ignoreChatCookies?: any) => {
		if (ignoreChatCookies) {
			return ignoreChatCookies === 'true';
		}
		return settings?.additionalSettings?.noChatCookies === true;
	}, []);

	const fetchPlayerConfigCallback = useCallback((desktopConfig?: any, mobileConfig?: any) => {
		if (fromSceneEditor) {
			return;
		}

		const settings = isUsedOnMobile(configParams ?? {}) ? mobileConfig : desktopConfig;
		if (Object.keys(settings).length === 0) {
			return;
		}

		const language = configParams.language ? configParams.language : settings?.accessibilitySettings?.language;
		if (language !== i18n.language) {
			i18n.changeLanguage(language);
			const bcp47Lang = language === 'dk' ? 'da' : language === 'no' ? 'nb' : language || 'en';
			document.documentElement.lang = bcp47Lang;
		}

		if (isIgnoreChatCookies(settings, configParams.ignoreChatCookies)) {
			Cookies.remove(`chat_ID_${accountId}`);
			Cookies.remove(`chat_name_${accountId}`);
			Cookies.remove(`chat_email_${accountId}`);
			setUserId('');
			setUsername('');
			setEmail('');
		}

		setAdditionalStyleSheet({
			settings,
			isMediaLive,
			paramSharing: configParams.sharing,
			paramDownload: configParams.downloadMedia,
			isBonnierDi: configParams.isBonnierDi
		});
	}, [fromSceneEditor, configParams, isIgnoreChatCookies, isMediaLive, accountId]);

	const setPlayerConfigData = useCallback((configData: any) => {
		if (configData && configData.layouts) {
			const desktop = configData.layouts.find((el: any) => {
				return el.type === 'desktop';
			});
			desktop && setPlayerDesktopConfig({ ...desktop, id: fromSceneEditor ? SCENE_EDITOR_PLAYER_CONFIG_ID : playerConfigId || ENTERPRISE_CONFIG_ID });

			const mobile = configData.layouts.find((el: any) => {
				return el.type === 'mobile';
			});
			mobile && setPlayerMobileConfig({ ...mobile, id: fromSceneEditor ? SCENE_EDITOR_PLAYER_CONFIG_ID : playerConfigId || ENTERPRISE_CONFIG_ID });

			setPlayerConfig(configData);
			setLoading?.(false);
		}
	}, [fromSceneEditor, playerConfigId, setLoading]);

	useEffect(() => {
		let configUrl = playerConfigURL || (accountId ? `${playerConfigDomain}${accountId}/settings/${playerConfigId || ENTERPRISE_CONFIG_ID}` : "");
		const enterpriseConfigUrl = `${playerConfigDomain}${accountId ?? SCENE_EDITOR_PLAYER_ACCOUNT_ID}/settings/${ENTERPRISE_CONFIG_ID}`;
		if (fromSceneEditor) {
			configUrl = `${playerConfigDomain}${SCENE_EDITOR_PLAYER_ACCOUNT_ID}/settings/${SCENE_EDITOR_PLAYER_CONFIG_ID}`;
		}
		if (!configUrl) {
			return;
		}

		axios.get(configUrl).then((response) => {
			if (response?.data) {
				setPlayerConfigData(response?.data);
			} else {
				axios.get(enterpriseConfigUrl).then((response) => {
					response?.data && setPlayerConfigData(response?.data);
				});
			}
		}).catch((_error) => {
			axios.get(enterpriseConfigUrl).then((response) => {
				response?.data && setPlayerConfigData(response?.data);
			});
		});
	}, [playerConfigId, accountId, fromSceneEditor, playerConfigURL, setPlayerConfigData]);

	useEffect(() => {
		fetchPlayerConfigCallback?.(playerDesktopConfig, playerMobileConfig);
	}, [playerMobileConfig, playerDesktopConfig, fetchPlayerConfigCallback]);

	return { playerConfig, playerMobileConfig, playerDesktopConfig };
};

export default useFetchPlayerConfigData;
